/* eslint-disable camelcase */
import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import DocumentsList from '../../components/AddOrEditPage/AddOrEditLoan/Sections/BorrowerInsurances/Insurance/DocumentsList';

import useForm from '../utils/useForm';

function TooltipContent({ entity }) {
  const {
    name,
    address_line_1,
    address_line_2,
    city,
    state,
    zip,
    website,
    phone,
  } = entity;

  // Construct address components conditionally
  const address = [address_line_1, address_line_2]
    .filter(Boolean) // Removes undefined, null, or empty strings
    .join(', ');

  const location = [city, state, zip].filter(Boolean).join(', ');

  return (
    <ul className="list-disc pl-3">
      {name && <li>{name}</li>}
      {address && <li>{address}</li>}
      {location && <li>{location}</li>}
      {website && (
        <li>
          <a
            className="!text-blue-600 !underline"
            href={website}
            target="_blank"
            rel="noopener noreferrer"
          >
            {website}
          </a>
        </li>
      )}
      {phone && (
        <li>
          <a className="!text-blue-600 !underline" href={`tel:${phone}`}>
            {phone}
          </a>
        </li>
      )}
    </ul>
  );
}

const useBorrowerInsuranceForm = (onSubmit, values) => {
  const { t } = useTranslation();

  const fields = useMemo(
    () => [
      {
        title: t(
          'AddOrEditPage.AddOrEditLoan.Sections.BorrowerInsurances.Insurance.Insurance'
        ),
        isTitleHidden: true,
        fields: [
          {
            title: t('common.forms.labels.Policy Coverage Type'),
            name: 'type',
            type: 'text',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Policy Number'),
            name: 'policy_number',
            type: 'text',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Payee'),
            name: '_carrier.name',
            type: 'textarea',
            readOnly: true,
            format: (vals) => {
              if (vals.send_payment_to === 'Broker') {
                return vals._broker?.name || '';
              }
              if (vals.send_payment_to === 'Carrier') {
                return vals._carrier?.name || '';
              }

              if (vals.send_payment_to === 'Agent') {
                return vals._agent?.name || '';
              }

              return '';
            },
            tooltip: (vals) => {
              return vals?._carrier.name ? (
                <TooltipContent entity={values?._carrier} />
              ) : null;
            },
          },
          {
            title: t('common.forms.labels.Carrier Contact'),
            name: '_carrier_contact.name',
            type: 'text',
            readOnly: true,
            tooltip: (vals) => {
              return vals?._carrier_contact?.name ? (
                <TooltipContent entity={values?._carrier_contact} />
              ) : null;
            },
          },
          {
            title: t('common.forms.labels.Agent'),
            name: '_agent.name',
            type: 'textarea',
            readOnly: true,
            tooltip: (vals) => {
              return vals?._agent?.name ? (
                <TooltipContent entity={values?._agent} />
              ) : null;
            },
          },
          {
            title: t('common.forms.labels.Agent Contact'),
            name: '_agent_contact.name',
            type: 'text',
            readOnly: true,
            tooltip: (vals) => {
              return vals?._agent_contact?.name ? (
                <TooltipContent entity={values?._agent_contact} />
              ) : null;
            },
          },
          {
            title: t('common.forms.labels.Premium'),
            name: 'premium ',
            type: 'currency',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Agent Contact'),
            name: '_agent_contact.name',
            type: 'text',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Start Date'),
            name: 'start_date',
            type: 'date',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.End Date'),
            name: 'end_date',
            type: 'date',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Dwelling Coverage'),
            name: 'dwelling_coverage',
            type: 'currency',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Other Structure Coverage'),
            name: 'other_structure_coverage',
            type: 'currency',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Supplemental Coverage'),
            name: 'supplemental_coverage',
            type: 'currency',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Deductible'),
            name: 'all_other_deductible_amount',
            type: 'currency',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Wind Deductible'),
            name: 'wind_deductible_amount',
            type: 'currency',
            readOnly: true,
          },
          {
            title: t('common.forms.labels.Documents'),
            name: 'documents',
            type: 'component',
            component: DocumentsList,
            props: {
              documents: values?.documents,
            },
          },
        ],
      },
    ],
    [
      values?._agent,
      values?._agent_contact,
      values?._carrier,
      values?._carrier_contact,
      values?.documents,
      t,
    ]
  );

  return useForm({ fields, onSubmit, values });
};

export default useBorrowerInsuranceForm;
